import { useEffect, useState } from "react";
import { ReceiptText, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/common";
import { DateRange } from "@/components/TxTable";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { daysAgoStr, fmtDateTime, FREQ_LABELS, RATES, rupiah, todayStr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MoneyInput } from "@/components/MoneyInput";

const WithdrawDialog = ({ open, onOpenChange, saldo, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/admin-fees/withdraw", { amount: Number(amount) }); toast.success("Biaya admin ditarik ke Kas Masuk · Pendapatan Jasa"); setAmount(""); onOpenChange(false); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="fee-withdraw-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tarik Biaya Admin</DialogTitle></DialogHeader>
        <p className="text-sm">Saldo: <b className="font-mono">{rupiah(saldo)}</b></p>
        <MoneyInput value={amount} onChange={setAmount} className="h-12 text-lg" testId="fee-withdraw-amount" />
        <DialogFooter><Button className="rounded-full h-11 px-6" disabled={busy || !Number(amount)} onClick={submit} data-testid="fee-withdraw-submit">Tarik</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function BiayaAdminPage() {
  const { user } = useAuth();
  const [range, setRange] = useState({ start: daysAgoStr(29), end: todayStr() });
  const [d, setD] = useState(null);
  const [wd, setWd] = useState(false);
  const showCabang = ["super_admin", "admin_wilayah"].includes(user.role);
  useEffect(() => { api.get("/admin-fees", { params: range }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [range]);

  return (
    <div>
      <PageHeader eyebrow={`Potongan ${RATES.admin}% pinjaman`} title="Biaya Administrasi"
        subtitle={user.role === "petugas" ? "Biaya admin dari pinjaman anggota yang Anda tangani." : "Rincian dan saldo biaya administrasi dari pencairan pinjaman anggota."}
        action={<div className="flex flex-wrap gap-2"><DateRange start={range.start} end={range.end} onChange={(start, end) => setRange({ start, end })} testId="fee-range" />
          {user.role === "kasir" && <Button className="h-11 rounded-full" onClick={() => setWd(true)} data-testid="fee-withdraw-button">Tarik Biaya Admin</Button>}</div>} />
      <WithdrawDialog open={wd} onOpenChange={setWd} saldo={d?.saldo || 0} onSaved={() => setRange({ ...range })} />
      {!d ? <p className="text-muted-foreground">Memuat...</p> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <StatCard label="Saldo Biaya Admin" value={rupiah(d.saldo)} hint={`Periode ${rupiah(d.total)} · ditarik ${rupiah(d.withdrawn)}`} icon={ReceiptText} testId="fee-total" />
            <StatCard label="Jumlah Pencairan" value={String(d.count)} icon={Users} tone="muted" testId="fee-count" />
            {!!d.by_petugas.length && (
              <div className="stat-card col-span-2 lg:col-span-1" data-testid="fee-by-petugas">
                <p className="eyebrow mb-3">Per Petugas Lapangan</p>
                <div className="space-y-1.5 max-h-28 overflow-y-auto">{d.by_petugas.map((p) => <p key={p.name} className="flex justify-between text-sm"><span className="truncate">{p.name}</span><span className="font-mono font-semibold">{rupiah(p.total)}</span></p>)}</div>
              </div>
            )}
          </div>
          <div className="panel !p-0 overflow-hidden">
            {d.rows.length === 0 ? <p className="text-sm text-muted-foreground p-10 text-center" data-testid="fee-empty">Belum ada biaya administrasi pada rentang ini.</p> : (
              <div className="overflow-x-auto" data-testid="fee-table">
                <Table>
                  <TableHeader><TableRow><TableHead className="pl-6">Tanggal</TableHead><TableHead>Anggota</TableHead>{showCabang && <TableHead>Cabang</TableHead>}<TableHead>Resort</TableHead><TableHead className="text-right">Pokok Pinjaman</TableHead><TableHead className="text-right">Biaya Admin</TableHead><TableHead className="pr-6">Petugas Lapangan</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {d.rows.map((r) => (
                      <TableRow key={r.id} data-testid={`fee-row-${r.id}`}>
                        <TableCell className="pl-6 text-sm whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                        <TableCell><p className="font-medium">{r.member_name}</p><p className="font-mono text-xs text-muted-foreground">{r.nik} · {FREQ_LABELS[r.frequency]}</p></TableCell>
                        {showCabang && <TableCell className="text-sm">{r.cabang_name || "-"}</TableCell>}
                        <TableCell className="text-sm">{r.resort_name || "-"}</TableCell>
                        <TableCell className="text-right font-mono">{rupiah(r.principal)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-primary">{rupiah(r.amount)}</TableCell>
                        <TableCell className="pr-6 text-sm">{r.petugas_name || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
